export const TUBE_ROUTES = {
  "butt-joint": "/printed-cardboard-tubes/butt-jointed-tubes",
  telescopic: "/printed-cardboard-tubes/telescopic-cardboard-tubes",
  "tin-lid": "/printed-cardboard-tubes/metal-lid-cardboard-tubes",
};

export const PRODUCT_PAGES = {
  "butt-jointed-tubes": {
    slug: "butt-jointed-tubes",
    styleId: "butt-joint",
    styleName: "Butt Joint",
    seoTitle: "Custom Printed Butt Jointed Tubes | Tube Boxes Direct",
    metaDescription:
      "Custom printed butt jointed cardboard tubes for candles, skincare and gifts. Flush-fitting lids, branded finishes and orders from 250 units. Request a quote.",
    h1: "Custom Printed Butt Jointed Tubes",
    intro:
      "A neat lid line. A strong shelf presence. Butt jointed tubes combine a rigid cardboard body with a short lift-off lid, creating a balanced format for branded retail packaging, gifts and product launches.",
    keyPoints: [
      "Flush-fitting lid and body.",
      "Custom printed exterior.",
      "Size and finish selected around your product.",
      "Orders from 250 units per design.",
    ],
    primaryCta: "Get a Butt Joint Tube Quote",
    secondaryCta: "Explore Sizes & Finishes",
    imageAlt: "Custom printed butt jointed cardboard tubes with short lift-off lids in several colours",
    overviewSections: [
      {
        heading: "A tidy closure with room for your brand",
        paragraphs: [
          "A butt jointed tube uses an inner shoulder to locate the lid. When closed, the outer lid and body meet at a visible joint, with their outside surfaces aligned.",
          "The short lid leaves most of the body available for your artwork. It is a versatile option for brands that want a straightforward opening experience and a considered, compact presentation.",
        ],
      },
      {
        heading: "Make the lid part of the design",
        paragraphs: [
          "Continue a pattern across the pack or use a contrasting lid to frame your branding. The joint position should be considered during artwork preparation so that important text and logos sit comfortably on the finished tube.",
          "For artwork that needs precise alignment across the joint, discuss the design with us before finalising the files.",
        ],
      },
    ],
    applicationsTitle: "Common applications",
    applications: [
      { title: "Candles & home fragrance", text: "Presentation packaging for candle vessels, diffusers and home fragrance gifts." },
      { title: "Beauty & skincare", text: "Branded outer packaging for jars, bottles and coordinated product collections." },
      { title: "Retail gifts", text: "A distinctive format for accessories, keepsakes and promotional products." },
      { title: "Bottles & speciality products", text: "A presentation option where dimensions, product weight and any internal support are agreed in advance." },
    ],
    specRows: [
      ["Construction", "Rigid cardboard body with inner shoulder and short lift-off lid."],
      ["Closure", "Lid and body meet at a flush outer joint."],
      ["Diameter", "Selected from the available range for this construction."],
      ["Height", "Confirmed to suit the product and agreed construction."],
      ["Print", "CMYK or Pantone colour matching, according to specification."],
      ["Finishes", "Hot foil and spot UV options, subject to material and artwork."],
      ["Minimum order", "From 250 units per design."],
      ["Dispatch", "From 3 weeks after artwork approval; confirmed with quotation."],
    ],
    specNote:
      "Internal dimensions describe the space available for your product. Allowance may also be needed for the shoulder, lid, product removal and any insert. Share the finished product dimensions before choosing a tube size.",
    printFinish: {
      heading: "Print & finish",
      paragraphs: [
        "A simple logo, a full-colour illustration or a repeating pattern can each give this format a different character. Choose a finish that supports your artwork and the way the pack will be handled.",
        "Discuss foil details and spot UV areas before artwork approval so that the design can be prepared for the selected production method.",
      ],
    },
    faqs: [
      {
        q: "What is a butt jointed tube?",
        a: "It is a cardboard tube with a short removable lid that fits over an inner shoulder. The lid and body align on the outside, meeting at a distinct joint.",
      },
      {
        q: "How is it different from a telescopic tube?",
        a: "A butt jointed tube has a short lid near the top. A telescopic tube uses a longer outer sleeve that slides over the inner body.",
      },
      {
        q: "Can the lid be a different colour?",
        a: "Yes. The lid and body can be designed in complementary or contrasting colours, with the final print specification confirmed in your quotation.",
      },
      {
        q: "Can you help choose the size?",
        a: "Send the product dimensions, weight and intended use. We can review these against the available tube sizes and identify any need for internal support.",
      },
      {
        q: "Can the tube be used as a shipping container?",
        a: "It is primarily a presentation pack. Shipping requirements should be assessed separately, and a protective outer carton may be needed.",
      },
    ],
    finalCta: {
      heading: "Give your product a considered first impression.",
      text: "Tell us what you are packing, the quantity and your preferred finish. We will help turn those details into a practical tube specification.",
      button: "Request My Butt Joint Tube Quote",
    },
  },

  "telescopic-cardboard-tubes": {
    slug: "telescopic-cardboard-tubes",
    styleId: "telescopic",
    styleName: "Telescopic",
    seoTitle: "Custom Printed Telescopic Tubes | Tube Boxes Direct",
    metaDescription:
      "Discover custom printed telescopic cardboard tubes with long sliding lids and contrasting bases. Premium presentation from 250 units. Request your quote.",
    h1: "Custom Printed Telescopic Cardboard Tubes",
    intro:
      "Create an opening experience that reveals your product gradually. A long outer lid slides over the inner body, giving telescopic tubes a distinctive silhouette and room for a second colour or message beneath the sleeve.",
    keyPoints: [
      "Long sliding outer lid.",
      "Contrasting base and inner-body options.",
      "A broad exterior surface for printed artwork.",
      "Orders from 250 units per design.",
    ],
    primaryCta: "Get a Telescopic Tube Quote",
    secondaryCta: "Explore Sizes & Finishes",
    imageAlt: "Custom printed telescopic cardboard tube with a long sliding outer lid in navy and red",
    overviewSections: [
      {
        heading: "Designed around the reveal",
        paragraphs: [
          "The outer sleeve covers most of the inner body when the tube is closed. Lifting the sleeve exposes the inner section and the product inside.",
          "The visible base can blend into the artwork or introduce a deliberate contrast. Sleeve length, overlap and product clearance are agreed as part of the construction.",
        ],
      },
      {
        heading: "Two surfaces. One coordinated design.",
        paragraphs: [
          "Use the outer sleeve for the main brand statement and the inner body for a complementary colour, a short message or a quieter finish.",
          "Plan what the customer sees both before and after opening. Keep essential product information accessible on the finished pack rather than relying only on an area hidden beneath the sleeve.",
        ],
      },
    ],
    applicationsTitle: "Common applications",
    applications: [
      { title: "Spirits & bottles", text: "Presentation packaging specified around bottle shape, weight and handling." },
      { title: "Confectionery gifts", text: "A branded outer pack for wrapped or separately contained confectionery." },
      { title: "Beauty collections", text: "A coordinated presentation for individual products or compact gift sets." },
      { title: "Launches & promotional gifts", text: "An opening format that introduces a second visual layer." },
    ],
    specRows: [
      ["Construction", "Inner cardboard body with a long overlapping outer lid."],
      ["Closure", "Sliding lift-off sleeve."],
      ["Base reveal", "Proportion and colour agreed during specification."],
      ["Diameter", "Selected from compatible available sizes."],
      ["Height and sleeve overlap", "Confirmed around the product and opening requirements."],
      ["Print", "CMYK or Pantone colour matching, according to specification."],
      ["Finishes", "Hot foil and spot UV options, subject to material and artwork."],
      ["Minimum order", "From 250 units per design."],
      ["Dispatch", "From 3 weeks after artwork approval; confirmed with quotation."],
    ],
    specNote:
      "Fit depends on more than the overall height. Product clearance, sleeve overlap and ease of removal should all be considered before artwork is finalised.",
    printFinish: {
      heading: "Print & finish",
      paragraphs: [
        "A long outer sleeve gives uninterrupted space for illustrations, typography and patterns. Contrasting colours on the sleeve and base can make the construction itself part of the branding.",
        "Select foil or spot UV details with the artwork layout in mind, and confirm any printing required on the inner body separately.",
      ],
    },
    faqs: [
      {
        q: "What makes a tube telescopic?",
        a: "It has an outer lid that slides over an inner body, with a longer overlap than a short-lid construction.",
      },
      {
        q: "Can the exposed base be a different colour?",
        a: "Yes. A contrasting base can be included in the design. The visible height and finish are confirmed during specification.",
      },
      {
        q: "Can you print the inner body?",
        a: "Please include this in your brief. The print area and production options need to be confirmed for the chosen construction.",
      },
      {
        q: "Is this an adjustable-length tube?",
        a: "This range is made to an agreed specification. The sliding lid describes the opening construction; it does not mean the finished pack is intended to provide an adjustable usable height.",
      },
      {
        q: "Is it suitable for heavy bottles?",
        a: "Bottle weight, dimensions and support requirements must be reviewed. Any insert and protective shipping packaging should be specified separately.",
      },
    ],
    finalCta: {
      heading: "Make the reveal part of your packaging.",
      text: "Share your product dimensions and the look you want to achieve. We will help define the sleeve, base and printed finish.",
      button: "Request My Telescopic Tube Quote",
    },
  },

  "metal-lid-cardboard-tubes": {
    slug: "metal-lid-cardboard-tubes",
    styleId: "tin-lid",
    styleName: "Tin Lid",
    seoTitle: "Custom Printed Metal Lid Tubes | Tube Boxes Direct",
    metaDescription:
      "Custom printed cardboard tubes with removable metal lids for gifts, retail and packaged food products. Orders from 250 units. Request a tailored quote.",
    h1: "Custom Printed Cardboard Tubes with Metal Lids",
    intro:
      "Combine a printed cardboard body with a removable metal lid for a distinctive canister-style pack. This format brings together generous branding space and a clearly defined metal closure.",
    keyPoints: [
      "Rigid cardboard tube body.",
      "Removable metal lid.",
      "Custom printed wrap.",
      "Orders from 250 units per design.",
    ],
    primaryCta: "Get a Metal Lid Tube Quote",
    secondaryCta: "Explore Sizes & Finishes",
    imageAlt: "Custom printed cardboard tube with a removable gold metal lid and navy printed body",
    overviewSections: [
      {
        heading: "A different finish at the top",
        paragraphs: [
          "The metal lid adds a contrasting material and a recognisable closure detail. The printed tube body remains the main canvas for branding, illustrations and product information.",
          "Lid fit, rim detail and base construction depend on the chosen format and should be confirmed with the overall specification.",
        ],
      },
      {
        heading: "Coordinate the lid with your artwork",
        paragraphs: [
          "Choose the printed body colour and lid finish together. A restrained design can draw attention to the metal detail, while an illustrated wrap can create a more decorative retail pack.",
          "Ask which lid finishes are available for your selected diameter — options are confirmed for the chosen construction during quotation.",
        ],
      },
    ],
    applicationsTitle: "Common applications",
    applications: [
      { title: "Tea & coffee presentation", text: "Outer packaging for products held in an appropriate sealed inner pack." },
      { title: "Confectionery & biscuits", text: "Gift packaging for wrapped or suitably contained food products." },
      { title: "Spirits & speciality bottles", text: "Branded presentation specified around the bottle and handling requirements." },
      { title: "Gifts & keepsakes", text: "Canister-style packaging for retail collections and promotional items." },
    ],
    specRows: [
      ["Construction", "Rigid cardboard body with a fitted removable metal lid."],
      ["Closure", "Metal lid; exact fit and rim detail confirmed by specification."],
      ["Base", "Confirmed for the selected format."],
      ["Lid finish", "Available options confirmed for the selected diameter."],
      ["Diameter", "Subject to compatible lid sizes."],
      ["Height", "Confirmed to suit the product and construction."],
      ["Print", "CMYK or Pantone colour matching, according to specification."],
      ["Finishes", "Hot foil and spot UV on compatible printed wraps."],
      ["Minimum order", "From 250 units per design."],
      ["Dispatch", "From 3 weeks after artwork approval; confirmed with quotation."],
    ],
    specNote: "",
    extraSections: [
      {
        heading: "Food packaging requirements",
        paragraphs: [
          "If you are packing food, tell us whether it will be wrapped, bagged or placed directly inside the tube. Direct food contact, moisture protection and aroma retention require an appropriate material and lining specification.",
          "A removable metal lid alone does not establish that the pack is airtight or suitable for direct food contact.",
        ],
      },
      {
        heading: "Materials & disposal",
        paragraphs: [
          "This construction combines paper-based and metal components. Disposal guidance should reflect the complete specification, including the wrap, finishes, lining and any inserts. Separate components where required by the local recycling service.",
        ],
      },
    ],
    printFinish: {
      heading: "Print & finish",
      paragraphs: [
        "A simple logo, a full-colour illustration or a repeating pattern can each give this format a different character. Choose a finish that supports your artwork and the way the pack will be handled.",
        "Discuss foil details and spot UV areas before artwork approval so that the design can be prepared for the selected production method.",
      ],
    },
    faqs: [
      {
        q: "What is a metal lid cardboard tube?",
        a: "It combines a rigid cardboard body with a removable metal closure, creating a canister-style presentation pack.",
      },
      {
        q: "Is it airtight?",
        a: "Not as a standard claim. If your product needs an airtight barrier, tell us before quotation so that the sealing and inner-pack requirements can be assessed.",
      },
      {
        q: "Can I pack tea or coffee directly inside?",
        a: "Direct food contact and barrier performance must be confirmed for the complete construction. A suitable sealed inner bag may be required.",
      },
      {
        q: "Can the lid have my logo on it?",
        a: "Ask us to confirm branding options for the selected lid. Body printing and lid decoration are separate specifications.",
      },
      {
        q: "Can it be recycled?",
        a: "This depends on the complete material specification and local facilities. Cardboard and metal components may need to be separated.",
      },
    ],
    finalCta: {
      heading: "Build a distinctive canister-style pack.",
      text: "Tell us about the product, quantity and preferred appearance. Include any food-contact or barrier requirements so these can be considered from the start.",
      button: "Request My Metal Lid Tube Quote",
    },
  },
};
